import type { ReactNode } from "react";
import { useGetList, useListContext } from "ra-core";

import { VOGELKOP_BUDGETS } from "../../../../supabase/functions/_shared/vogelkop.ts";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Campaign, LeadSource, Sale } from "../types";

const Field = ({ label, children }: { label: string; children: ReactNode }) => (
  <label className="flex min-w-[9rem] flex-1 flex-col gap-1 text-xs text-muted-foreground">
    <span>{label}</span>
    {children}
  </label>
);

const controlClass =
  "h-8 rounded-md border bg-transparent px-2 text-sm text-foreground";

const localDayStart = (value: string) => {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day).toISOString();
};

export const LeadFilterBar = () => {
  const { filterValues, setFilters } = useListContext();
  const { dealStages, dealCategories } = useConfigurationContext();
  const { data: sources = [] } = useGetList<LeadSource>("lead_sources", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "name", order: "ASC" },
  });
  const { data: campaigns = [] } = useGetList<Campaign>("campaigns", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "name", order: "ASC" },
  });
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: { page: 1, perPage: 20 },
    sort: { field: "last_name", order: "ASC" },
  });

  const set = (patch: Record<string, unknown>) => {
    setFilters({ ...filterValues, ...patch }, undefined);
  };

  const createdFrom =
    typeof filterValues["first_seen@gte"] === "string"
      ? String(filterValues["first_seen@gte"]).slice(0, 10)
      : "";
  const lastFrom =
    typeof filterValues["last_seen@gte"] === "string"
      ? String(filterValues["last_seen@gte"]).slice(0, 10)
      : "";

  return (
    <div className="mb-4 grid grid-cols-2 gap-3 rounded-xl border bg-card p-3 md:grid-cols-4 xl:grid-cols-8">
      <Field label="Created">
        <input
          type="date"
          aria-label="Created"
          className={controlClass}
          value={createdFrom}
          onChange={(event) =>
            set({
              "first_seen@gte": event.target.value
                ? localDayStart(event.target.value)
                : undefined,
            })
          }
        />
      </Field>
      <Field label="Stage">
        <select
          aria-label="Stage"
          className={controlClass}
          value={
            typeof filterValues.stage === "string" ? filterValues.stage : ""
          }
          onChange={(event) => set({ stage: event.target.value || undefined })}
        >
          <option value="">Any</option>
          {dealStages.map((stage) => (
            <option key={stage.value} value={stage.value}>
              {stage.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Services">
        <select
          aria-label="Services"
          className={controlClass}
          value={
            typeof filterValues["services@cs"] === "string"
              ? String(filterValues["services@cs"]).replace(/[{}]/g, "")
              : ""
          }
          onChange={(event) =>
            set({
              "services@cs": event.target.value
                ? `{${event.target.value}}`
                : undefined,
            })
          }
        >
          <option value="">Any</option>
          {dealCategories.map((service) => (
            <option key={service.value} value={service.value}>
              {service.label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Source">
        <select
          aria-label="Source"
          className={controlClass}
          value={
            filterValues.source_id == null ? "" : String(filterValues.source_id)
          }
          onChange={(event) =>
            set({
              source_id: event.target.value
                ? Number(event.target.value)
                : undefined,
            })
          }
        >
          <option value="">Any</option>
          {sources.map((source) => (
            <option key={source.id} value={String(source.id)}>
              {source.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Campaign">
        <select
          aria-label="Campaign"
          className={controlClass}
          value={
            filterValues.campaign_id == null
              ? ""
              : String(filterValues.campaign_id)
          }
          onChange={(event) =>
            set({
              campaign_id: event.target.value
                ? Number(event.target.value)
                : undefined,
            })
          }
        >
          <option value="">Any</option>
          {campaigns.map((campaign) => (
            <option key={campaign.id} value={String(campaign.id)}>
              {campaign.name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Owner">
        <select
          aria-label="Owner"
          className={controlClass}
          value={
            filterValues.sales_id == null ? "" : String(filterValues.sales_id)
          }
          onChange={(event) =>
            set({
              sales_id: event.target.value
                ? Number(event.target.value)
                : undefined,
              "sales_id@is": undefined,
            })
          }
        >
          <option value="">Any</option>
          {sales.map((sale) => (
            <option key={sale.id} value={String(sale.id)}>
              {sale.first_name} {sale.last_name}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Budget">
        <select
          aria-label="Budget"
          className={controlClass}
          value={
            typeof filterValues.budget === "string" ? filterValues.budget : ""
          }
          onChange={(event) => set({ budget: event.target.value || undefined })}
        >
          <option value="">Any</option>
          {VOGELKOP_BUDGETS.map((budget) => (
            <option key={budget} value={budget}>
              {budget}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Last activity">
        <input
          type="date"
          aria-label="Last activity"
          className={controlClass}
          value={lastFrom}
          onChange={(event) =>
            set({
              "last_seen@gte": event.target.value
                ? localDayStart(event.target.value)
                : undefined,
            })
          }
        />
      </Field>
    </div>
  );
};
